import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useForm, type FieldErrors } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, FileText, Loader2, UploadCloud, X } from 'lucide-react'
import { Button, Dropdown } from '../../../../components'
import { CustomDatePicker } from '../../../../components/CustomDatePicker'
import { showToastMessage } from '../../../../components/Toast'
import { resolveBranchId } from '../../../../shared/utils/branchUtils'
import { getApiErrorMessage, extractErrorMessage } from '../../../../shared/utils/errorUtils'
import {
  createLeavePolicy,
  stageLeavePolicyAttachment,
  updateLeavePolicy,
} from '../api/hrmsLeavePolicies.api'
import {
  getHrmsLeavePolicyDetailQueryKey,
  HRMS_LEAVE_POLICIES_QUERY_KEY,
  useLeavePolicyDetail,
  useStaffTypeOptions,
} from '../hooks/useLeavePolicies'
import {
  createLeavePolicySchema,
  getLocalTodayDateString,
  type LeavePolicyFormValues,
} from '../schemas/leavePolicySchema'
import type {
  CreateLeavePolicyPayload,
  LeavePolicyDto,
  StageAttachmentResponse,
  UpdateLeavePolicyPayload,
} from '../types/hrmsLeavePolicies.types'

export interface AddLeavePolicyDrawerProps {
  open: boolean
  onClose: () => void
  mode?: 'create' | 'edit'
  policyId?: string | null
  existingPolicies?: LeavePolicyDto[]
  onSave?: (data: LeavePolicyFormValues, id?: string) => Promise<void>
  initialData?: LeavePolicyDto | null
}

export function AddLeavePolicyDrawer({
  open,
  onClose,
  mode = 'create',
  policyId,
  existingPolicies = [],
}: AddLeavePolicyDrawerProps) {
  const queryClient = useQueryClient()
  const isEditMode = mode === 'edit' || Boolean(policyId)

  // Fetch staff type lookup options for employee group dropdown
  const {
    options: staffTypeLookupOptions,
    isLoading: isStaffTypesLoading,
    isError: isStaffTypesError,
    error: staffTypesError,
    refetch: refetchStaffTypes,
  } = useStaffTypeOptions()

  // Fetch full policy detail in edit mode
  const {
    data: detailData,
    isLoading: isDetailLoading,
    isError: isDetailError,
    error: detailError,
    refetch: refetchDetail,
  } = useLeavePolicyDetail(policyId, open && isEditMode)

  const todayLocal = useMemo(() => getLocalTodayDateString(), [])

  // Dynamically ensure the policy's staffTypeId is represented in dropdown options on Edit
  const employeeGroupOptions = useMemo(() => {
    const opts = [...staffTypeLookupOptions]
    if (detailData?.staffTypeId) {
      const exists = opts.some((o) => o.value === detailData.staffTypeId)
      if (!exists) {
        opts.push({
          label: detailData.staffType?.displayName || detailData.staffTypeId,
          value: detailData.staffTypeId,
        })
      }
    }
    return opts
  }, [staffTypeLookupOptions, detailData?.staffTypeId, detailData?.staffType?.displayName])

  const schema = useMemo(() => {
    return createLeavePolicySchema(existingPolicies, policyId || undefined, {
      mode: isEditMode ? 'edit' : 'create',
      initialEffectiveFrom: isEditMode ? detailData?.effectiveFrom || null : null,
    })
  }, [existingPolicies, policyId, isEditMode, detailData?.effectiveFrom])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    reset,
    formState: { errors },
  } = useForm<LeavePolicyFormValues>({
    resolver: zodResolver(schema) as any,
    mode: 'onChange',
    defaultValues: {
      policyName: '',
      employeeGroup: '',
      annualEntitlement: 12,
      compensationNature: 'Paid',
      status: true,
      advancedEnabled: false,
      workingDays: null,
      earnedLeaves: null,
      permitCarryForward: true,
      maxCarryForwardLimit: 5,
      effectiveFrom: todayLocal,
      attachmentStagingKey: null,
      attachmentName: null,
    },
  })

  // Watched fields
  const status = watch('status')
  const employeeGroup = watch('employeeGroup')
  const compensationNature = watch('compensationNature')
  const advancedEnabled = watch('advancedEnabled')
  const permitCarryForward = watch('permitCarryForward')
  const effectiveFrom = watch('effectiveFrom')

  // Attachment state
  const [isDragging, setIsDragging] = useState(false)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [stagedAttachment, setStagedAttachment] = useState<StageAttachmentResponse | null>(null)
  const [existingAttachment, setExistingAttachment] = useState<{
    fileName: string
    fileUrl: string
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const formRef = useRef<HTMLFormElement | null>(null)

  // Mutations
  const createMutation = useMutation({
    mutationFn: (payload: CreateLeavePolicyPayload) => createLeavePolicy(payload),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLeavePolicyPayload }) =>
      updateLeavePolicy(id, payload),
  })

  // Handle detail 404/403 errors on fetch
  useEffect(() => {
    if (open && isEditMode && isDetailError) {
      const status = (detailError as any)?.response?.status
      if (status === 404) {
        showToastMessage(getApiErrorMessage(detailError, 'Policy no longer exists'), 'error')
        onClose()
        queryClient.invalidateQueries({ queryKey: HRMS_LEAVE_POLICIES_QUERY_KEY })
      } else if (status === 403) {
        showToastMessage(
          getApiErrorMessage(detailError, 'You do not have permission to view this leave policy.'),
          'error'
        )
      }
    }
  }, [open, isEditMode, isDetailError, detailError, onClose, queryClient])

  // Synchronize form values on detail arrival or mode change
  useEffect(() => {
    if (open) {
      setAttachmentError(null)
      setIsUploadingFile(false)

      if (isEditMode) {
        if (detailData && detailData.id === policyId) {
          setStagedAttachment(null)
          if (detailData.attachmentUrl) {
            setExistingAttachment({
              fileName: detailData.attachmentName || 'Attachment',
              fileUrl: detailData.attachmentUrl,
            })
          } else {
            setExistingAttachment(null)
          }

          reset({
            policyName: detailData.name || '',
            employeeGroup: detailData.staffTypeId || '',
            annualEntitlement: detailData.annualEntitlementDays ?? 12,
            compensationNature: detailData.isPaid !== false ? 'Paid' : 'Unpaid',
            status:
              detailData.status?.toUpperCase() === 'ACTIVE' || detailData.status === 'Active',
            advancedEnabled: Boolean(
              detailData.advanceSetting ||
                (detailData.workingDay && detailData.workingDay > 0)
            ),
            workingDays: detailData.workingDay ?? null,
            earnedLeaves: detailData.earnedLeaves ?? null,
            permitCarryForward: Boolean(
              detailData.allowCarryForward &&
                (detailData.maxCarryForwardDays ?? 0) > 0
            ),
            maxCarryForwardLimit: detailData.maxCarryForwardDays ?? 0,
            effectiveFrom: detailData.effectiveFrom || todayLocal,
            attachmentStagingKey: null,
            attachmentName: null,
          })
        }
      } else {
        setStagedAttachment(null)
        setExistingAttachment(null)
        reset({
          policyName: '',
          employeeGroup: '',
          annualEntitlement: 12,
          compensationNature: 'Paid',
          status: true,
          advancedEnabled: false,
          workingDays: null,
          earnedLeaves: null,
          permitCarryForward: true,
          maxCarryForwardLimit: 5,
          effectiveFrom: todayLocal,
          attachmentStagingKey: null,
          attachmentName: null,
        })
      }
    }
  }, [open, isEditMode, policyId, detailData, reset, todayLocal])

  // Close on Escape key
  useEffect(() => {
    if (!open) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose])

  // Handle toggles
  const handleToggleAdvanced = (val: boolean) => {
    setValue('advancedEnabled', val, { shouldValidate: true, shouldDirty: true })
    if (!val) {
      setValue('workingDays', null)
      setValue('earnedLeaves', null)
      clearErrors(['workingDays', 'earnedLeaves'])
    }
  }

  const handleToggleCarryForward = (val: boolean) => {
    setValue('permitCarryForward', val, { shouldValidate: true, shouldDirty: true })
    if (!val) {
      setValue('maxCarryForwardLimit', 0)
      clearErrors('maxCarryForwardLimit')
    }
  }

  // File staging helper
  const handleFileUpload = async (incomingFiles: FileList | File[]) => {
    setAttachmentError(null)
    const filesArray = Array.from(incomingFiles)
    if (filesArray.length === 0) return

    const file = filesArray[0]
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const validExtensions = ['pdf', 'jpeg', 'jpg', 'png', 'webp', 'docx']
    const validMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'image/webp',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ]

    const isValidExt = validExtensions.includes(ext)
    const isValidMime = validMimeTypes.includes(file.type)

    if (!isValidExt && !isValidMime) {
      setAttachmentError('Allowed formats: PDF, JPEG, PNG, WEBP, DOCX')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      setAttachmentError('File must be 10 MB or smaller')
      return
    }

    setIsUploadingFile(true)
    try {
      const staged = await stageLeavePolicyAttachment(file)
      setStagedAttachment(staged)
      setExistingAttachment(null)
      setValue('attachmentStagingKey', staged.stagingKey, { shouldDirty: true })
      setValue('attachmentName', staged.fileName, { shouldDirty: true })
      showToastMessage(`${staged.fileName} staged successfully.`, 'info')
    } catch (err: any) {
      const errorMsg = getApiErrorMessage(err, 'Failed to upload attachment.')
      setAttachmentError(errorMsg)
      showToastMessage(errorMsg, 'error')
    } finally {
      setIsUploadingFile(false)
    }
  }

  const handleRemoveStagedFile = () => {
    setAttachmentError(null)
    setStagedAttachment(null)
    setValue('attachmentStagingKey', null, { shouldDirty: true })
    setValue('attachmentName', null, { shouldDirty: true })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleRemoveExistingFile = () => {
    setAttachmentError(null)
    // TODO: Removing an existing attachment on edit is currently a no-op for the API payload
    // because the backend contract for deleting an existing attachment is undocumented.
    setExistingAttachment(null)
  }

  // Submit Handler
  const onSubmit = async (values: LeavePolicyFormValues) => {
    if (isEditMode) {
      if (!policyId) {
        showToastMessage('Policy ID is missing for edit.', 'error')
        return
      }

      // Build PATCH payload with editable fields only (NEVER send id, branchId, staffType, createdAt, updatedAt)
      const payload: UpdateLeavePolicyPayload = {
        name: values.policyName.trim(),
        staffTypeId: values.employeeGroup,
        annualEntitlementDays: Number(values.annualEntitlement),
        isPaid: values.compensationNature === 'Paid',
        allowCarryForward: Boolean(values.permitCarryForward),
        maxCarryForwardDays: values.permitCarryForward ? Number(values.maxCarryForwardLimit) : 0,
        effectiveFrom: values.effectiveFrom,
        status: values.status ? 'ACTIVE' : 'INACTIVE',
        advanceSetting: Boolean(values.advancedEnabled),
        workingDay:
          values.advancedEnabled && values.workingDays != null ? Number(values.workingDays) : null,
        earnedLeaves:
          values.advancedEnabled && values.earnedLeaves != null ? Number(values.earnedLeaves) : null,
      }

      // ATTACHMENT RULES on edit:
      // * File unchanged: OMIT attachmentStagingKey, attachmentUrl, attachmentName entirely.
      // * New file picked: send attachmentStagingKey + attachmentUrl (same as staging key) + attachmentName (fileName).
      // * User removes existing file: no-op for API payload, omitted.
      if (stagedAttachment) {
        payload.attachmentStagingKey = stagedAttachment.stagingKey
        payload.attachmentUrl = stagedAttachment.stagingKey
        payload.attachmentName = stagedAttachment.fileName
      }

      try {
        await updateMutation.mutateAsync({ id: policyId, payload })
        showToastMessage('Leave policy updated successfully.', 'success')
        queryClient.invalidateQueries({ queryKey: HRMS_LEAVE_POLICIES_QUERY_KEY })
        const branchId = resolveBranchId() || ''
        queryClient.invalidateQueries({
          queryKey: getHrmsLeavePolicyDetailQueryKey(policyId, branchId),
        })
        onClose()
        reset()
        setStagedAttachment(null)
        setExistingAttachment(null)
      } catch (err: any) {
        const status = err?.response?.status
        if (status === 409) {
          setError('policyName', {
            type: 'manual',
            message: getApiErrorMessage(err, 'Policy name already exists'),
          })
          return
        }

        if (status === 404) {
          showToastMessage(getApiErrorMessage(err, 'Policy no longer exists'), 'error')
          onClose()
          queryClient.invalidateQueries({ queryKey: HRMS_LEAVE_POLICIES_QUERY_KEY })
          return
        }

        if (status === 403) {
          showToastMessage(
            getApiErrorMessage(err, 'You do not have permission to update leave policies.'),
            'error'
          )
          return
        }

        const errorMsg = getApiErrorMessage(err, 'Failed to update leave policy.')
        showToastMessage(errorMsg, 'error')
      }
      return
    }

    // Build POST payload for create
    const payload: CreateLeavePolicyPayload = {
      name: values.policyName.trim(),
      staffTypeId: values.employeeGroup,
      annualEntitlementDays: Number(values.annualEntitlement),
      isPaid: values.compensationNature === 'Paid',
      allowCarryForward: Boolean(values.permitCarryForward),
      maxCarryForwardDays: values.permitCarryForward ? Number(values.maxCarryForwardLimit) : 0,
      effectiveFrom: values.effectiveFrom,
      status: values.status ? 'ACTIVE' : 'INACTIVE',
      advanceSetting: Boolean(values.advancedEnabled),
      workingDay:
        values.advancedEnabled && values.workingDays != null ? Number(values.workingDays) : null,
      earnedLeaves:
        values.advancedEnabled && values.earnedLeaves != null ? Number(values.earnedLeaves) : null,
    }

    if (stagedAttachment) {
      payload.attachmentStagingKey = stagedAttachment.stagingKey
      payload.attachmentUrl = stagedAttachment.stagingKey
      payload.attachmentName = stagedAttachment.fileName
    }

    try {
      await createMutation.mutateAsync(payload)
      showToastMessage('Leave policy created successfully.', 'success')
      queryClient.invalidateQueries({ queryKey: HRMS_LEAVE_POLICIES_QUERY_KEY })
      onClose()
      reset()
      setStagedAttachment(null)
      setExistingAttachment(null)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 409) {
        setError('policyName', {
          type: 'manual',
          message: getApiErrorMessage(err, 'Policy name already exists'),
        })
        return
      }

      if (status === 403) {
        showToastMessage(
          getApiErrorMessage(err, 'You do not have permission to create leave policies.'),
          'error'
        )
        return
      }

      const errorMsg = getApiErrorMessage(err, 'Failed to create leave policy.')
      showToastMessage(errorMsg, 'error')
    }
  }

  const onInvalid = (fieldErrors: FieldErrors<LeavePolicyFormValues>) => {
    console.error('[AddLeavePolicyDrawer] Validation errors on submit:', fieldErrors)
    const firstError = Object.values(fieldErrors)[0]
    const errorMsg = firstError?.message || 'Please resolve validation errors before saving.'
    showToastMessage(String(errorMsg), 'error')

    if (formRef.current) {
      const firstErrorEl = formRef.current.querySelector<HTMLElement>(
        '[data-error="true"], .border-red-500'
      )
      if (firstErrorEl) {
        firstErrorEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
        firstErrorEl.focus?.()
      }
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending
  const isSubmitDisabled = isSaving || isUploadingFile || (isEditMode && isDetailLoading)

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-[999] font-sans">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={() => {
          onClose()
          reset()
        }}
      />

      {/* Drawer Panel */}
      <aside className="fixed top-0 right-0 h-screen w-full max-w-[420px] bg-white shadow-xl rounded-l-2xl flex flex-col overflow-hidden z-[1000] border-l border-slate-200">
        {/* Header */}
        <div className="bg-gray-50 px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-teal-900 text-white flex items-center justify-center shrink-0">
              <FileText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-900 truncate">
                {isEditMode ? 'Edit Leave Policy' : 'Add Leave Policy'}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5 leading-snug">
                {isEditMode
                  ? 'Update leave policy rules and entitlements.'
                  : 'Create a leave policy and assign it to an employee group.'}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              onClose()
              reset()
            }}
            className="rounded-lg p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition cursor-pointer shrink-0 ml-auto"
            aria-label="Close drawer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Form Body / Loading / Error State */}
        {isEditMode && isDetailLoading ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 space-y-3 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin text-[#146971]" />
            <p className="text-sm font-medium">Loading policy details...</p>
          </div>
        ) : isEditMode && isDetailError ? (
          <div className="flex-1 p-6 font-sans">
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-800 space-y-3">
              <h3 className="text-base font-bold">Failed to load policy details</h3>
              <p className="text-sm text-red-600">
                {extractErrorMessage(detailError, 'An error occurred while fetching policy details.')}
              </p>
              <Button variant="secondary" size="sm" onClick={() => refetchDetail()}>
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit(onSubmit as any, onInvalid as any)}
            className="flex-1 overflow-y-auto p-5 space-y-4 min-w-0 overflow-x-hidden"
          >
            {/* Section 1 — Basic Details */}
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-2">
                <span className="uppercase text-[11px] font-semibold tracking-wide text-teal-800">
                  SECTION 1 — BASIC DETAILS
                </span>

                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs font-semibold text-slate-600">Status</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={status}
                    onClick={() => setValue('status', !status, { shouldValidate: true })}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      status ? 'bg-[#146971]' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs transition duration-200 ease-in-out ${
                        status ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Field 1: Policy Name */}
              <div className="flex flex-col gap-1.5">
                <label className="block uppercase text-[11px] font-semibold tracking-wide text-gray-500">
                  POLICY NAME
                </label>
                <input
                  type="text"
                  {...register('policyName')}
                  placeholder="e.g., Teaching Staff – Casual Leave"
                  data-error={Boolean(errors.policyName)}
                  className={`h-11 w-full bg-gray-100 rounded-lg px-3.5 text-sm text-slate-900 outline-none transition-all ${
                    errors.policyName
                      ? 'border border-red-500 bg-red-50/20'
                      : 'border border-transparent focus:bg-white focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10'
                  }`}
                />
                {errors.policyName && (
                  <p className="text-xs text-red-600 font-medium">{errors.policyName.message}</p>
                )}
              </div>

              {/* Field 2: Employee Group */}
              <div className="flex flex-col gap-1.5">
                <label className="block uppercase text-[11px] font-semibold tracking-wide text-gray-500">
                  EMPLOYEE GROUP
                </label>
                <div data-error={Boolean(errors.employeeGroup || isStaffTypesError)}>
                  <Dropdown
                    options={employeeGroupOptions}
                    value={employeeGroup}
                    placeholder={
                      isStaffTypesLoading
                        ? 'Loading employee groups...'
                        : isStaffTypesError
                        ? 'Failed to load employee groups'
                        : 'Select Employee Group'
                    }
                    disabled={isStaffTypesLoading || isStaffTypesError}
                    onChange={(val) =>
                      setValue('employeeGroup', val, { shouldValidate: true, shouldDirty: true })
                    }
                    variant="filled"
                    buttonClassName={`h-11 w-full rounded-lg border bg-gray-100 px-3.5 text-sm text-slate-900 transition-all ${
                      errors.employeeGroup || isStaffTypesError
                        ? 'border-red-500 bg-red-50/20'
                        : 'border-transparent focus:bg-white focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10'
                    }`}
                    menuClassName="mt-1 z-[1050]"
                    placement="bottom"
                  />
                </div>
                {isStaffTypesError && (
                  <div className="flex items-center justify-between text-xs text-red-600 font-medium">
                    <span>
                      Failed to load employee groups:{' '}
                      {extractErrorMessage(staffTypesError, 'Please check your connection.')}
                    </span>
                    <button
                      type="button"
                      onClick={() => refetchStaffTypes()}
                      className="text-xs text-[#146971] underline font-semibold hover:text-[#0f565d] ml-2 shrink-0 cursor-pointer"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {errors.employeeGroup && !isStaffTypesError && (
                  <p className="text-xs text-red-600 font-medium">{errors.employeeGroup.message}</p>
                )}
              </div>
            </div>

            {/* Section 2 Card — Leave Entitlement */}
            <div className="rounded-xl bg-gray-100 p-4 space-y-4 border border-slate-200/60">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Section 2 — Leave Entitlement</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Defines how much leave is available during the leave year.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Annual Entitlement */}
                <div className="flex flex-col gap-1.5">
                  <label className="block uppercase text-[11px] font-semibold tracking-wide text-gray-500">
                    ANNUAL ENTITLEMENT
                  </label>
                  <div className="relative flex items-center">
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      max="365"
                      {...register('annualEntitlement')}
                      data-error={Boolean(errors.annualEntitlement)}
                      className={`h-11 w-full rounded-lg bg-white pr-16 pl-3.5 text-sm font-medium text-slate-900 outline-none transition-all ${
                        errors.annualEntitlement
                          ? 'border border-red-500 bg-red-50/20'
                          : 'border border-slate-200 focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10'
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400 pointer-events-none whitespace-nowrap">
                      days / yr
                    </span>
                  </div>
                  {errors.annualEntitlement && (
                    <p className="text-xs text-red-600 font-medium">
                      {errors.annualEntitlement.message}
                    </p>
                  )}
                </div>

                {/* Compensation Nature */}
                <div className="flex flex-col gap-1.5">
                  <label className="block uppercase text-[11px] font-semibold tracking-wide text-gray-500">
                    COMPENSATION NATURE
                  </label>
                  <div className="rounded-lg bg-gray-200 p-1 flex gap-1 h-11 items-center">
                    <button
                      type="button"
                      onClick={() => setValue('compensationNature', 'Paid', { shouldValidate: true })}
                      className={`flex-1 h-9 rounded-md text-sm transition-all cursor-pointer ${
                        compensationNature === 'Paid'
                          ? 'bg-white shadow-xs font-semibold text-slate-900'
                          : 'text-slate-600 font-medium hover:text-slate-900'
                      }`}
                    >
                      Paid
                    </button>
                    <button
                      type="button"
                      onClick={() => setValue('compensationNature', 'Unpaid', { shouldValidate: true })}
                      className={`flex-1 h-9 rounded-md text-sm transition-all cursor-pointer ${
                        compensationNature === 'Unpaid'
                          ? 'bg-white shadow-xs font-semibold text-slate-900'
                          : 'text-slate-600 font-medium hover:text-slate-900'
                      }`}
                    >
                      Unpaid
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3 Card — Advanced Setting */}
            <div className="rounded-xl bg-gray-100 p-4 space-y-4 border border-slate-200/60">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Section 3 — Advanced Setting</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Set additional leave entitlement rules.
                  </p>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={advancedEnabled}
                  onClick={() => handleToggleAdvanced(!advancedEnabled)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    advancedEnabled ? 'bg-[#146971]' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs transition duration-200 ease-in-out ${
                      advancedEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {advancedEnabled && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 border-t border-slate-200/60">
                  {/* Working Days */}
                  <div className="flex flex-col gap-1.5" data-error={Boolean(errors.workingDays)}>
                    <label className="block uppercase text-[11px] font-semibold tracking-wide text-gray-500">
                      WORKING DAYS
                    </label>
                    <div className="relative flex items-center">
                      <input
                        type="number"
                        placeholder="e.g., 15"
                        {...register('workingDays', { valueAsNumber: true })}
                        className={`h-11 w-full rounded-lg bg-white pr-16 pl-3.5 text-sm font-medium text-slate-900 outline-none transition-all ${
                          errors.workingDays
                            ? 'border border-red-500 bg-red-50/20'
                            : 'border border-slate-200 focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10'
                        }`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400 pointer-events-none whitespace-nowrap">
                        days
                      </span>
                    </div>
                    {errors.workingDays && (
                      <p className="text-xs text-red-600 font-medium">{errors.workingDays.message}</p>
                    )}
                  </div>

                  {/* Earned Leaves */}
                  <div className="flex flex-col gap-1.5" data-error={Boolean(errors.earnedLeaves)}>
                    <label className="block uppercase text-[11px] font-semibold tracking-wide text-gray-500">
                      EARNED LEAVES
                    </label>
                    <div className="relative flex items-center">
                      <input
                        type="number"
                        step="0.01"
                        placeholder="e.g., 1"
                        {...register('earnedLeaves', { valueAsNumber: true })}
                        className={`h-11 w-full rounded-lg bg-white pr-16 pl-3.5 text-sm font-medium text-slate-900 outline-none transition-all ${
                          errors.earnedLeaves
                            ? 'border border-red-500 bg-red-50/20'
                            : 'border border-slate-200 focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10'
                        }`}
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400 pointer-events-none whitespace-nowrap">
                        days
                      </span>
                    </div>
                    {errors.earnedLeaves && (
                      <p className="text-xs text-red-600 font-medium">
                        {errors.earnedLeaves.message}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Section 4 Card — Carry Forward */}
            <div className="rounded-xl bg-gray-100 p-4 space-y-4 border border-slate-200/60">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">Section 4 — Carry Forward</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Permit unused leave balances to roll over to subsequent year.
                  </p>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={permitCarryForward}
                  onClick={() => handleToggleCarryForward(!permitCarryForward)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    permitCarryForward ? 'bg-[#146971]' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-xs transition duration-200 ease-in-out ${
                      permitCarryForward ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex flex-col gap-1.5 pt-1 border-t border-slate-200/60">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    Maximum Carry Forward Limit:
                  </span>
                  <div className="relative flex items-center">
                    <input
                      type="number"
                      min="0"
                      disabled={!permitCarryForward}
                      {...register('maxCarryForwardLimit')}
                      data-error={Boolean(errors.maxCarryForwardLimit)}
                      className={`w-28 h-11 rounded-lg bg-white pr-16 pl-3.5 text-sm font-medium text-slate-900 outline-none transition-all disabled:bg-slate-200/60 disabled:text-slate-400 disabled:cursor-not-allowed ${
                        errors.maxCarryForwardLimit && permitCarryForward
                          ? 'border border-red-500 bg-red-50/20'
                          : 'border border-slate-200 focus:border-[#146971] focus:ring-4 focus:ring-[#146971]/10'
                      }`}
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-400 pointer-events-none">
                      days
                    </span>
                  </div>
                </div>
                {errors.maxCarryForwardLimit && permitCarryForward && (
                  <p className="text-xs text-red-600 font-medium">
                    {errors.maxCarryForwardLimit.message}
                  </p>
                )}
              </div>
            </div>

            {/* Section 5 Card — Policy Period */}
            <div className="rounded-xl bg-gray-100 p-4 space-y-4 border border-slate-200/60">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Section 5 — Policy Period</h3>
                <p className="text-xs text-slate-500 mt-0.5">Effective window for calendar accruals.</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="block uppercase text-[11px] font-semibold tracking-wide text-gray-500">
                  EFFECTIVE FROM
                </label>
                <div data-error={Boolean(errors.effectiveFrom)}>
                  <CustomDatePicker
                    value={effectiveFrom}
                    onChange={(val) =>
                      setValue('effectiveFrom', val, { shouldValidate: true, shouldDirty: true })
                    }
                    placeholder={todayLocal}
                    minDate={todayLocal}
                    calendarClassName="!z-[1100]"
                    buttonClassName={`w-full h-11 px-3.5 rounded-lg bg-white text-sm font-normal text-slate-900 shadow-xs border ${
                      errors.effectiveFrom
                        ? 'border-red-500 bg-red-50/20'
                        : 'border-slate-200 hover:border-[#146971]'
                    }`}
                  />
                </div>
                {errors.effectiveFrom && (
                  <p className="text-xs text-red-600 font-medium">{errors.effectiveFrom.message}</p>
                )}
              </div>
            </div>

            {/* Section 6 Card — Attachment */}
            <div className="rounded-xl bg-gray-100 p-4 space-y-4 border border-slate-200/60">
              <div>
                <h3 className="text-sm font-bold text-slate-900">Section 6 — Attachment</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Attach documents related to the leave policy.
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpeg,.jpg,.png,.webp,.docx"
                onChange={(e) => {
                  if (e.target.files) {
                    handleFileUpload(e.target.files)
                  }
                }}
                className="hidden"
              />

              {isUploadingFile ? (
                <div className="rounded-xl border-2 border-dashed border-[#146971] bg-teal-50/50 p-6 flex flex-col items-center justify-center text-center">
                  <Loader2 className="w-8 h-8 text-[#146971] animate-spin mb-2" />
                  <p className="font-semibold text-sm text-slate-800">Uploading attachment...</p>
                  <p className="text-xs text-slate-500 mt-1">Please wait while the file is staged</p>
                </div>
              ) : stagedAttachment ? (
                <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-teal-600 text-white flex items-center justify-center shrink-0">
                      <Check className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 truncate" title={stagedAttachment.fileName}>
                        {stagedAttachment.fileName}
                      </p>
                      {stagedAttachment.fileSizeBytes > 0 && (
                        <p className="text-[11px] text-slate-500">
                          {stagedAttachment.fileSizeBytes < 1024 * 1024
                            ? `${(stagedAttachment.fileSizeBytes / 1024).toFixed(0)} KB`
                            : `${(stagedAttachment.fileSizeBytes / (1024 * 1024)).toFixed(1)} MB`}
                        </p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    aria-label="Remove attachment"
                    onClick={handleRemoveStagedFile}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition cursor-pointer shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : existingAttachment ? (
                <div className="rounded-xl border border-teal-200 bg-teal-50/60 p-4 flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg bg-teal-900 text-white flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <a
                        href={existingAttachment.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-teal-900 hover:text-teal-950 underline truncate block"
                        title={existingAttachment.fileName}
                      >
                        {existingAttachment.fileName}
                      </a>
                      <p className="text-[11px] text-teal-700 mt-0.5">Existing policy attachment</p>
                    </div>
                  </div>

                  <button
                    type="button"
                    aria-label="Remove attachment"
                    onClick={handleRemoveExistingFile}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition cursor-pointer shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => fileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      fileInputRef.current?.click()
                    }
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    setIsDragging(true)
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault()
                    setIsDragging(false)
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    setIsDragging(false)
                    if (e.dataTransfer.files) {
                      handleFileUpload(e.dataTransfer.files)
                    }
                  }}
                  className={`rounded-xl border-2 border-dashed bg-white p-6 flex flex-col items-center justify-center text-center transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-teal-700 ${
                    isDragging
                      ? 'border-[#146971] bg-teal-50/50'
                      : 'border-slate-200 hover:border-[#146971]'
                  }`}
                >
                  <div className="w-16 h-16 rounded-full bg-cyan-100 flex items-center justify-center mb-3 shrink-0">
                    <UploadCloud className="w-8 h-8 text-[#146971]" />
                  </div>
                  <p className="font-semibold text-sm text-slate-800">
                    Drag &amp; drop file here or{' '}
                    <span className="text-[#146971] font-bold underline">Browse Files</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Supported formats: PDF, JPEG, PNG, WEBP, DOCX • Maximum file size: 10 MB
                  </p>
                </div>
              )}

              {attachmentError && (
                <p className="text-xs text-red-600 font-medium">{String(attachmentError)}</p>
              )}
            </div>
          </form>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-3 shrink-0">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              onClose()
              reset()
            }}
            className="bg-white hover:bg-slate-100 text-slate-700 border-slate-200 h-10 px-4 text-xs font-semibold rounded-lg shadow-xs"
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={isSubmitDisabled}
            icon={isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            onClick={() => {
              handleSubmit(onSubmit as any, onInvalid as any)()
            }}
            className="bg-teal-900 hover:bg-teal-950 text-white h-10 px-5 text-xs font-semibold rounded-lg shadow-xs disabled:opacity-50 cursor-pointer"
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Policy' : 'Save Policy'}
          </Button>
        </div>
      </aside>
    </div>,
    document.body
  )
}
